import { CommonModule, Location } from '@angular/common';
import { Component, computed, effect, inject, OnDestroy, OnInit, AfterViewInit, signal, ViewChild, ElementRef, HostListener, ChangeDetectorRef } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { matTooltipSmartImports } from '@app/shared/imports/mat-tooltip-smart';
import { Router, ActivatedRoute } from '@angular/router';
import { ApiService } from '@app/core/services/API/api-service';
import { AuthService } from '@app/core/services/Auth/auth-service';
import { NavigationService } from '@app/core/services/sidebar/sidebar.service';
import { Title, Meta } from '@angular/platform-browser';
import { CustomPaginatorComponent } from '@app/shared/components/custom-paginator/custom-paginator';
import { Spinner } from '@app/shared/components/spinner/spinner';
import { Subject, takeUntil, finalize } from 'rxjs';
import { AddPeopleForm } from '@app/pages/people/add-people-form/add-people-form';
import { EditPeopleForm } from '@app/pages/people/edit-people-form/edit-people-form';
import { ConfirmExport } from '@app/shared/components/confirm-export/confirm-export';
import { ConfirmPopup } from '@app/shared/components/confirm-popup/confirm-popup';
import { ToastrService } from 'ngx-toastr';
import { SocketService } from '@app/core/services/socket/socket.service';
import { TableHorizontalScrollController } from '@app/shared/utils/table-horizontal-scroll.util';
import { PaginatorPageSize, parsePaginatorPageSize } from '@app/shared/utils/paginator-page-size.util';
import { CopyableTruncateTooltipDirective } from '@app/shared/directives/copyable-truncate-tooltip.directive';
export interface AccessLevel {
  _id: string;
  description: string;
  accessDetails: any[];
  accesslevel_name: string;
  createdAt: string;
  createdBy: string;
  default: boolean;
  en_id: string;
  liscense_name: string;
  liscense_type: string;
  status: string;
  updatedAt: string;
  __v: number;
}

export interface Person {
  _id: string;
  name: string;
  email: string;
  image: string;
  phone: string;
  homeTeamID: string;
  jobInfo: string;
  jobRoleID: string;
  timezone: string;
  country: string;
  managerID: string;
  utype: string;
  accesslevel: string | AccessLevel;
  accesslevelType?: string;
  accessLevelDetails?: AccessLevel;
  isActive: boolean;
  isOnline?: boolean;
  status: string;
  createdBy: string;
  lastUpdatedByID?: string;
  createdAt: string;
  updatedAt: string;
  address: string;
  dateOfBirth?: string | Date;
  lastSeen?: string;
  lastLoggedIn?: string;
}

export interface TableHeader {
  fieldName: string;
  prop: string;
}

export interface TableHeaders {
  _id: string;
  list: TableHeader[];
  moduleName: string;
  objCode: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

export interface PeopleApiResponse {
  success: boolean;
  message: string;
  data: {
    tableHeaders: TableHeaders;
    userDetails: Person[];
  };
}

@Component({
  selector: 'app-people-listing',
  imports: [CommonModule, FormsModule, MatIconModule, MatSortModule, MatTableModule, ...matTooltipSmartImports, CopyableTruncateTooltipDirective, CustomPaginatorComponent, Spinner, AddPeopleForm, EditPeopleForm, ConfirmExport,ConfirmPopup],
  templateUrl: './people-listing.html',
  styleUrl: './people-listing.scss'
})
export class PeopleListing implements OnInit, AfterViewInit, OnDestroy {
  private destroy$ = new Subject<void>();
  @ViewChild('peopleListingTableTopScroll') private peopleListingTableTopScroll?: ElementRef<HTMLElement>;
  @ViewChild('peopleListingTableBottomScroll') private peopleListingTableBottomScroll?: ElementRef<HTMLElement>;

  // Angular 20 signals for state management
  people = signal<Person[]>([]);
  tableHeaders = signal<TableHeader[]>([]);
  loading = signal(false);
  error = signal<string | null>(null);
  searchTerm = signal('');
  searchText = signal('');
  selectedPeople = signal<string[]>([]);
  statusFilter = signal<'active' | 'inactive'>('active');

  // Data source for Material table
  dataSource: Person[] = [];
  paginatedDataSource: Person[] = [];

  // Slider state signals
  showAddPeopleSlider = signal(false);
  showEditPeopleSlider = signal(false);
  selectedPersonForEdit = signal<Person | null>(null);

  // Export modal state
  showExportModal = signal(false);
  //Delete
  showDeleteConfirmPopup = signal(false);
  deleteLoading = signal(false);
//Deactivate
  showDeactivateConfirmPopup = signal(false);
  deactivateLoading = signal(false);
//Activate
  showActivateConfirmPopup = signal(false);
  activateLoading = signal(false);

  // Pagination signals
  currentPage = signal(1);
  pageSize = signal(20);
  pageSizeOptions = [20, 50, 100];
  totalItems = signal(0);
  // Sort
  currentSortColumn = signal<string | null>(null);
  currentSortDirection = signal<'asc' | 'desc'>('asc');
  private toastr = inject(ToastrService);
  private authService = inject(AuthService);
  private navigationService = inject(NavigationService);
  private location = inject(Location);
  readonly tableHScroll: TableHorizontalScrollController;
  loginAsLoadingId = signal<string | null>(null);
  readonly isManagerUser = computed(
    () => this.authService.userData()?.accesslevelType === 'Manager'
  );
  readonly currentUserId = computed(() =>
    String(this.authService.userData()?._id ?? '').trim()
  );

  constructor(
    private apiService: ApiService, 
    private router: Router,
    private route: ActivatedRoute,
    private titleService: Title,
    private metaService: Meta,
    private socketService: SocketService,
    private cdr: ChangeDetectorRef
  ) {
    this.tableHScroll = new TableHorizontalScrollController(this.cdr);
    // Set up effect to reset pagination when search term changes
    effect(() => {
      const term = this.searchTerm();
      if (term) {
        this.currentPage.set(1);
      }
    });
  }

  ngOnInit(): void {
    this.loadPeople();
    this.listenToSocketPresence();

    // For meta title and description
    this.route.data.subscribe((data: { [key: string]: any }) => {
      if (data['title']) {
        this.titleService.setTitle(data['title']);
      }

      if (data['description']) {
        this.metaService.updateTag({
          name: 'description',
          content: data['description']
        });
      }
    });
  }

  ngAfterViewInit(): void {
    this.tableHScroll.bind(
      () => this.peopleListingTableTopScroll?.nativeElement,
      () => this.peopleListingTableBottomScroll?.nativeElement
    );
    setTimeout(() => this.tableHScroll.scheduleRefresh(), 0);
  }

  ngOnDestroy(): void {
    this.tableHScroll.dispose();
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('window:resize')
  onPeopleTableWindowResize(): void {
    this.tableHScroll.scheduleRefresh();
  }

  // Computed properties using Angular 20 signals
  filteredPeople = computed(() => {
    const people = this.people();
    const term = this.searchTerm().toLowerCase().trim();
    const status = this.statusFilter();

    let filtered = people;

    filtered = filtered.filter(person => {
      if (status === 'active') {
        return person.isActive === true;
      } else {
        return person.isActive === false;
      }
    });

    // Apply search filter
    if (term) {
      filtered = filtered.filter(person =>
        (person.name && String(person.name).toLowerCase().includes(term)) ||
        (person.email && String(person.email).toLowerCase().includes(term)) ||
        (person.phone && String(person.phone).toLowerCase().includes(term)) ||
        (person.address && String(person.address).toLowerCase().includes(term)) ||
        (person.timezone && String(person.timezone).toLowerCase().includes(term))
      );
    }

    return filtered;
  });

  paginatedPeople = computed(() => {
    const filtered = this.filteredPeople();
    const page = this.currentPage();
    const size = this.pageSize();
    const startIndex = (page - 1) * size;
    const endIndex = startIndex + size;

    return filtered.slice(startIndex, endIndex);
  });

  totalPages = computed(() => {
    return Math.ceil(this.filteredPeople().length / this.pageSize());
  });

  activeUsersCount = computed(() => this.people().filter(person => person.isActive === true).length);
  inactiveUsersCount = computed(() => this.people().filter(person => person.isActive === false).length);
  statusUsersCount = computed(() => this.statusFilter() === 'active' ? this.activeUsersCount() : this.inactiveUsersCount());

  hasSelection = computed(() => this.selectedPeople().length > 0);
  isAllSelected = computed(() => {
    const filtered = this.paginatedPeople();
    const selected = this.selectedPeople();
    return filtered.length > 0 && filtered.every(person => selected.includes(person._id));
  });
  isIndeterminate = computed(() => {
    const filtered = this.paginatedPeople();
    const selected = this.selectedPeople();
    return selected.length > 0 && selected.length < filtered.length;
  });

  hasSelectedActivePeople = computed(() => {
    const filteredPeople = this.filteredPeople();
    return this.selectedPeople().some(id => filteredPeople.find(p => p._id === id)?.isActive === true);
  });
  hasSelectedInactivePeople = computed(() => {
    const filteredPeople = this.filteredPeople();
    return this.selectedPeople().some(id => filteredPeople.find(p => p._id === id)?.isActive === false);
  });

  // Combined loading state for page overlay
  isPageLoading = computed(() => 
    this.loading() || 
    this.deleteLoading() || 
    this.deactivateLoading() || 
    this.activateLoading()
  );

  loadPeople(): void {
    this.loading.set(true);
    this.error.set(null);

    this.apiService.getAllPeople()
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.loading.set(false))
      )
      .subscribe({
        next: (response: PeopleApiResponse) => {
          if (response.success) {
            this.people.set(response.data.userDetails);
            this.tableHeaders.set(response.data.tableHeaders.list);
            this.totalItems.set(response.data.userDetails.length);
            this.updateDataSource();
            this.tableHScroll.scheduleRefresh();
          } else {
            this.error.set(response.message || 'Failed to load people data');
            this.tableHScroll.scheduleRefresh();
          }
        },
        error: (error) => {
          console.error('Error loading people:', error);
          this.error.set('Failed to load people data. Please try again.');
          this.tableHScroll.scheduleRefresh();
        }
      });
  }

  getFieldValue(person: Person, prop: string, fieldName?: string): any {
    if (prop === 'accesslevel') {
      return this.getAccessLevelDisplayName(person);
    }
    const raw = this.readPersonField(person, prop);
    if (this.isLastLoggedInColumn(prop, fieldName)) {
      return this.formatLastLoggedInDate(raw);
    }
    return raw;
  }

  /** Reads flat or dotted header props, e.g. jobRoleDetails.jobrole. */
  private readPersonField(person: Person, prop: string): unknown {
    if (!prop) {
      return undefined;
    }
    return prop.split('.').reduce<unknown>((current, key) => {
      if (current == null || typeof current !== 'object') {
        return undefined;
      }
      return (current as Record<string, unknown>)[key];
    }, person);
  }

  getAccessLevelDisplayName(person: Person): string {
    const accesslevel = person.accesslevel;
    if (accesslevel && typeof accesslevel === 'object' && accesslevel.accesslevel_name) {
      return accesslevel.accesslevel_name;
    }
    return (
      person.accessLevelDetails?.accesslevel_name ||
      person.accesslevelType ||
      person.utype ||
      ''
    );
  }

  isLastLoggedInColumn(prop: string, fieldName?: string): boolean {
    const normalizedProp = prop.toLowerCase().replace(/[^a-z0-9]/g, '');
    const normalizedField = (fieldName ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
    return (
      normalizedProp.includes('lastlogged') ||
      normalizedProp.includes('lastlogin') ||
      normalizedProp.includes('lastseen') ||
      normalizedProp.includes('lastactive') ||
      normalizedField.includes('lastlogged') ||
      normalizedField.includes('lastlogin') ||
      normalizedField.includes('lastseen') ||
      normalizedField.includes('lastactive')
    );
  }

  isLoginColumn(prop: string, fieldName?: string): boolean {
    if (this.isLastLoggedInColumn(prop, fieldName)) {
      return false;
    }
    const normalizedProp = prop.toLowerCase().replace(/[^a-z0-9]/g, '');
    const normalizedField = (fieldName ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
    return (
      normalizedProp === 'login' ||
      normalizedField === 'login' ||
      normalizedProp === 'loginas' ||
      normalizedField === 'loginas'
    );
  }

  isLoggedInUser(person: Person | null | undefined): boolean {
    const personId = String(person?._id ?? '').trim();
    const loggedInId = this.currentUserId();
    return !!personId && !!loggedInId && personId === loggedInId;
  }

  canLoginAsUser(person: Person | null | undefined): boolean {
    return !!person?._id && !this.isLoggedInUser(person);
  }

  loginAsUser(person: Person, event: Event): void {
    event.stopPropagation();
    event.preventDefault();

    if (!this.canLoginAsUser(person) || this.loginAsLoadingId()) {
      return;
    }

    this.loginAsLoadingId.set(person._id);
    this.authService.loginAs(person._id).pipe(
      takeUntil(this.destroy$),
      finalize(() => this.loginAsLoadingId.set(null))
    ).subscribe({
      next: (response: any) => {
        const userData = response?.data?.userData || response?.userData;
        this.navigationService.reloadNavigation();
        localStorage.removeItem('redirectUrl');
        const redirectPath = userData?.redirectTo || '/dashboard';
        this.router.navigateByUrl(redirectPath, { replaceUrl: true });
        this.toastr.success(`Logged in as ${person.name}`);
      },
      error: (error) => {
        const message = error?.error?.message || 'Failed to login as user. Please try again.';
        this.toastr.error(message);
      }
    });
  }

  private formatLastLoggedInDate(value: unknown): string {
    if (value == null || value === '') {
      return '';
    }
    if (typeof value === 'string' && value.trim().toUpperCase() === 'N/A') {
      return 'N/A';
    }
    const date = value instanceof Date ? value : new Date(String(value));
    if (Number.isNaN(date.getTime())) {
      return String(value);
    }
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  }

  /** Full address string for copyable overflow tooltip (empty when no address). */
  addressTooltipSource(person: Person, prop: string): string {
    const v = this.getFieldValue(person, prop);
    if (v == null) return '';
    return String(v).trim();
  }

   getStatusClass(status: string): string {
    if (status === 'online') {
      return 'status-online';
    } else if (status === 'offline') {
      return 'status-offline';
    }
    return status === 'active' ? 'status-active' : 'status-inactive';
  }

  onSearchChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.searchTerm.set(target.value);
  }

  refreshData(): void {
    this.loadPeople();
  }

  trackByPersonId(index: number, person: Person): string {
    return person._id;
  }

  // Checkbox functionality
  isPersonSelected(personId: string): boolean {
    return this.selectedPeople().includes(personId);
  }

  togglePersonSelection(personId: string, event: Event): void {
    const target = event.target as HTMLInputElement;
    const currentSelected = this.selectedPeople();

    if (target.checked) {
      if (!currentSelected.includes(personId)) {
        this.selectedPeople.set([...currentSelected, personId]);
      }
    } else {
      this.selectedPeople.set(currentSelected.filter(id => id !== personId));
    }
  }

  toggleAllSelection(event: Event): void {
    const target = event.target as HTMLInputElement;
    const filtered = this.paginatedPeople();

    if (target.checked) {
      this.selectedPeople.set(filtered.map(person => person._id));
    } else {
      this.selectedPeople.set([]);
    }
  }

  clearSelection(): void {
    this.selectedPeople.set([]);
  }

  // Pagination methods
  onPageChange(page: number): void {
    this.currentPage.set(page);
    this.updatePaginatedDataSource();
    this.tableHScroll.scheduleRefresh();
  }

  onPageSizeChange(raw: PaginatorPageSize): void {
    const size = parsePaginatorPageSize(raw, this.pageSize());
    this.pageSize.set(size);
    this.currentPage.set(1);
    this.updatePaginatedDataSource();
    this.tableHScroll.scheduleRefresh();
  }

  retryLoading(): void {
    this.loadPeople();
  }

  goBack(): void {
    // Implement navigation back logic
    window.history.back();
  }

  navigateToTeams(): void {
    this.router.navigate(['/people/teams']);
  }

  navigateToPersonView(personId: string): void {
    this.router.navigate(['/people/peopleview', personId]);
  }

  personViewHref(personId: string | undefined | null): string {
    const id = (personId || '').trim();
    if (!id) {
      return '';
    }
    const url = this.router.serializeUrl(this.router.createUrlTree(['/people/peopleview', id]));
    return this.location.prepareExternalUrl(url);
  }

  getDisplayedColumns(): string[] {
    const headerCols = this.tableHeaders().map(h => h.prop);
    return this.isManagerUser() ? ['select', ...headerCols] : headerCols;
  }

  // Search methods
  onSearchInput(): void {
    this.searchTerm.set(this.searchText());
    this.updateDataSource();
    this.tableHScroll.scheduleRefresh();
  }

  onSearch(): void {
    this.searchTerm.set(this.searchText());
    this.updateDataSource();
    this.tableHScroll.scheduleRefresh();
  }

  clearSearch(): void {
    this.searchText.set('');
    this.searchTerm.set('');
    this.updateDataSource();
    this.tableHScroll.scheduleRefresh();
  }

  /**
   * Subscribe to socket events to get live online/offline status updates
   */
  private listenToSocketPresence(): void {
    this.socketService.onlineUsers$
      .pipe(takeUntil(this.destroy$))
      .subscribe((payload) => this.updateOnlineStatusFromPayload(payload));

    this.socketService.userOffline$
      .pipe(takeUntil(this.destroy$))
      .subscribe((payload) => {
        const userId = payload?.userId || payload?._id || payload?.id || payload;
        if (userId) {
          this.setUserOnlineStatus(String(userId), false);
        }
      });
  }

  setStatusFilter(status: 'active' | 'inactive'): void {
    this.statusFilter.set(status);
    this.currentPage.set(1); // Reset to first page when filter changes
    this.updateDataSource();
    this.tableHScroll.scheduleRefresh();
  }

  toggleStatusFilter(): void {
    const currentStatus = this.statusFilter();
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    this.setStatusFilter(newStatus);
  }

  onStatusToggleChange(event: Event): void {
    const checked = (event.target as HTMLInputElement | null)?.checked === true;
    this.setStatusFilter(checked ? 'active' : 'inactive');
  }

  // Data check methods
  hasData(): boolean {
    return this.people().length > 0;
  }

  hasSelectedPeople(): boolean {
    return this.selectedPeople().length > 0;
  }

  hasSingleSelectedPeople(): boolean {
    return this.selectedPeople().length === 1;
  }

  hasMultipleSelectedPeople(): boolean {
    return this.selectedPeople().length > 1;
  }

  // Action methods
  openAddPeopleSliderWithoutParent(): void {
    this.showAddPeopleSlider.set(true);
  }

  closeAddPeopleSlider(): void {
    this.showAddPeopleSlider.set(false);
  }

  closeEditPeopleSlider(): void {
    this.showEditPeopleSlider.set(false);
    this.selectedPersonForEdit.set(null);
  }

  handlePersonSubmitted(data: any): void {
    // Handle the submitted data from the add form
    console.log('Person submitted:', data);

    // Optimistic update - add the new person to the current list
    const currentPeople = this.people();
    const newPerson: Person = {
      _id: data._id || this.generateId(),
      name: data.name,
      email: data.email,
      image: data.image || '',
      phone: data.phone ? String(data.phone) : '',
      homeTeamID: data.homeTeamID,
      jobInfo: data.jobInfo,
      jobRoleID: data.jobRoleID,
      timezone: data.timezone,
      country: data.country || '',
      managerID: data.managerID,
      utype: data.utype || 'user',
      accesslevel: data.accesslevel,
      isActive: data.isActive !== false,
      isOnline: data.isOnline !== undefined ? data.isOnline : false,
      status: data.isActive ? 'active' : 'inactive',
      createdBy: data.createdBy || '',
      lastUpdatedByID: data.lastUpdatedByID,
      createdAt: data.createdAt || new Date().toISOString(),
      updatedAt: data.updatedAt || new Date().toISOString(),
      address: data.address || '',
      dateOfBirth: data.dateOfBirth
    };

    // Add to the beginning of the list
    this.people.set([newPerson, ...currentPeople]);
    this.totalItems.set(this.people().length);
    this.updateDataSource();

    this.closeAddPeopleSlider();
  }

  handlePersonUpdated(data: any): void {
    // Handle the updated data from the edit form
    console.log('Person updated:', data);

    // Optimistic update - update the person in the current list
    const currentPeople = this.people();
    const updatedPeople = currentPeople.map(person => {
      if (person._id === data._id) {
        // Ensure phone is a string if it exists
        const updatedData = { ...data };
        if (updatedData.phone !== undefined) {
          updatedData.phone = updatedData.phone ? String(updatedData.phone) : '';
        }
        return { ...person, ...updatedData, updatedAt: new Date().toISOString() };
      }
      return person;
    });

    this.people.set(updatedPeople);
    this.updateDataSource();

    this.closeEditPeopleSlider();
  }

  openExportModal(): void {
    this.showExportModal.set(true);
  }

  closeExportModal(): void {
    this.showExportModal.set(false);
  }

  onExportConfirm(data: { emails: string[] }): void {
    const isActive = this.statusFilter() === 'active';
    this.apiService.exportPeople(data.emails, isActive)
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (response) => {
          console.log('Export successful:', response);
          this.toastr.success('People list exported successfully');
          // Close the modal after successful export
          this.closeExportModal();
        },
        error: (error) => {
          console.error('Export failed:', error);
          this.toastr.error(error.message || 'Failed to export people');
        }
      });
  }

  openEditPeopleSlider(): void {
    const selectedPersonId = this.selectedPeople()[0];
    const selectedPerson = this.people().find(person => person._id === selectedPersonId);
    if (selectedPerson) {
      this.selectedPersonForEdit.set(selectedPerson);
      this.showEditPeopleSlider.set(true);
    }
  }

  openEditMultiplePeopleForm(): void {
    console.log('Open edit multiple people form for:', this.selectedPeople());
    // Implement edit multiple people functionality
  }

  openDeletePeoplePopup(): void {
    if (this.selectedPeople().length > 0) {
      this.showDeleteConfirmPopup.set(true);
    }
  }

  showConfirmSharePeoplePopup(): void {
    console.log('Show share people popup for:', this.selectedPeople());
    // Implement share people functionality
  }

  addNewPerson(): void {
    // Navigate to add people form or open modal
    // This would typically navigate to the add-people-form component
    console.log('Navigate to add people form');
    // You can implement navigation logic here
    // this.router.navigate(['/people/add']);
  }

  editSelectedPeople(): void {
    // Navigate to edit people form or open modal for selected people
    const selectedIds = this.selectedPeople();
    console.log('Edit selected people:', selectedIds);
    // You can implement navigation logic here
    // this.router.navigate(['/people/edit'], { queryParams: { ids: selectedIds.join(',') } });
  }


  deleteSelectedPeople(): void {
    const selectedIds = this.selectedPeople();
    if (selectedIds.length === 0) {
      return;
    }

    // Store original data for rollback
    const originalPeople = [...this.people()];

    // Optimistic update - remove selected people from the list
    const remainingPeople = this.people().filter(person => !selectedIds.includes(person._id));
    this.people.set(remainingPeople);
    this.totalItems.set(this.people().length);
    this.updateDataSource();

    this.deleteLoading.set(true);
    this.apiService.deletePeople(selectedIds)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.deleteLoading.set(false))
      )
      .subscribe({
        next: (response) => {
          console.log('Delete successful:', response);
          this.selectedPeople.set([]);
          this.closeDeleteConfirmPopup();
        },
        error: (error) => {
          console.error('Delete failed:', error);
          // Rollback on error
          this.people.set(originalPeople);
          this.totalItems.set(this.people().length);
          this.updateDataSource();
          this.error.set('Failed to delete people. Please try again.');
        }
      });
  }

  closeDeleteConfirmPopup(): void {
    this.showDeleteConfirmPopup.set(false);
  }

  onDeleteConfirm(): void {
    this.deleteSelectedPeople();
  }

  onDeleteCancel(): void {
    this.closeDeleteConfirmPopup();
  }

   // Deactivate methods
   openDeactivatePeoplePopup(): void {
    if (this.selectedPeople().length > 0) {
      this.showDeactivateConfirmPopup.set(true);
    }
  }

  deactivateSelectedPeople(): void {
    const selectedIds = this.selectedPeople();
    if (selectedIds.length === 0) {
      return;
    }

    // Store original data for rollback
    const originalPeople = [...this.people()];

    // Optimistic update - update people status to inactive
    const updatedPeople = this.people().map(person =>
      selectedIds.includes(person._id)
        ? { ...person, isActive: false, status: 'inactive', updatedAt: new Date().toISOString() }
        : person
    );
    this.people.set(updatedPeople);
    this.updateDataSource();

    this.deactivateLoading.set(true);
    this.apiService.deactivatePeople(selectedIds)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.deactivateLoading.set(false))
      )
      .subscribe({
        next: (response) => {
          console.log('Deactivate successful:', response);
          this.toastr.success(response.message || 'Person Deactivate successfully!');
          this.selectedPeople.set([]);
          this.closeDeactivateConfirmPopup();
        },
        error: (error) => {
          console.error('Deactivate failed:', error);
          // Rollback on error
          this.people.set(originalPeople);
          this.updateDataSource();
          this.toastr.error(error.message || 'Failed to deactivate people. Please try again.');
          this.error.set('Failed to deactivate people. Please try again.');
        }
      });
  }

  closeDeactivateConfirmPopup(): void {
    this.showDeactivateConfirmPopup.set(false);
  }

  onDeactivateConfirm(): void {
    this.deactivateSelectedPeople();
  }

  onDeactivateCancel(): void {
    this.closeDeactivateConfirmPopup();
  }

  // Activate methods
  openActivatePeoplePopup(): void {
    if (this.selectedPeople().length > 0) {
      this.showActivateConfirmPopup.set(true);
    }
  }

  activateSelectedPeople(): void {
    const selectedIds = this.selectedPeople();
    if (selectedIds.length === 0) {
      return;
    }

    // Store original data for rollback
    const originalPeople = [...this.people()];

    // Optimistic update - update people status to active
    const updatedPeople = this.people().map(person =>
      selectedIds.includes(person._id)
        ? { ...person, isActive: true, status: 'active', updatedAt: new Date().toISOString() }
        : person
    );
    this.people.set(updatedPeople);
    this.updateDataSource();

    this.activateLoading.set(true);
    this.apiService.activatePeople(selectedIds)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.activateLoading.set(false))
      )
      .subscribe({
        next: (response) => {
          console.log('Activate successful:', response);
          this.toastr.success(response.message || 'Person Activate successfully!');
          this.selectedPeople.set([]);
          this.closeActivateConfirmPopup();
        },
        error: (error) => {
          console.error('Activate failed:', error);
          // Rollback on error
          this.people.set(originalPeople);
          this.updateDataSource();
          this.toastr.error(error.message || 'Failed to activate people. Please try again.');
          this.error.set('Failed to activate people. Please try again.');
        }
      });
  }

  closeActivateConfirmPopup(): void {
    this.showActivateConfirmPopup.set(false);
  }

  onActivateConfirm(): void {
    this.activateSelectedPeople();
  }

  onActivateCancel(): void {
    this.closeActivateConfirmPopup();
  }

  // Update data source based on current filters
  updateDataSource(): void {
    this.dataSource = this.filteredPeople();
    
    // Re-apply sort if there's an active sort to preserve sort order during socket updates
    const sortColumn = this.currentSortColumn();
    const sortDirection = this.currentSortDirection();
    
    if (sortColumn) {
      this.applySort(sortColumn, sortDirection);
    } else {
      this.updatePaginatedDataSource();
    }
    this.tableHScroll.scheduleRefresh();
  }
  
  /**
   * Apply sorting to the data source
   * This method is used both for user-initiated sorting and to preserve sort order during updates
   */
  private applySort(column: string, direction: 'asc' | 'desc'): void {
    // Sort the full data source
    this.dataSource = [...this.dataSource].sort((a, b) => {
      const isAsc = direction === 'asc';

      let aValue = this.getFieldValue(a, column);
      let bValue = this.getFieldValue(b, column);

      // Handle null/undefined values
      if (aValue === null || aValue === undefined) return isAsc ? 1 : -1;
      if (bValue === null || bValue === undefined) return isAsc ? -1 : 1;

      // Convert to strings for comparison
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();

      if (aStr < bStr) return isAsc ? -1 : 1;
      if (aStr > bStr) return isAsc ? 1 : -1;
      return 0;
    });

    // Update paginated data source
    this.updatePaginatedDataSource();
  }

  // Update paginated data source
  updatePaginatedDataSource(): void {
    const page = this.currentPage();
    const size = this.pageSize();
    const startIndex = (page - 1) * size;
    const endIndex = startIndex + size;

    this.paginatedDataSource = this.dataSource.slice(startIndex, endIndex);
    this.tableHScroll.scheduleRefresh();
  }

  /**
   * Handle incoming presence payloads from socket and update UI
   */
  private updateOnlineStatusFromPayload(payload: any): void {
    const statusMap = this.normalizeOnlineStatusPayload(payload);
    if (statusMap.size === 0) {
      return;
    }
    this.applyOnlineStatusMap(statusMap);
  }

  /**
   * Normalize different socket payload shapes into a userId -> isOnline map
   */
  private normalizeOnlineStatusPayload(payload: any): Map<string, boolean> {
    const statusMap = new Map<string, boolean>();
    if (!payload) {
      return statusMap;
    }

    // Single user payload e.g. { userId, isOnline, lastSeen }
    if (payload.userId && Object.prototype.hasOwnProperty.call(payload, 'isOnline')) {
      statusMap.set(String(payload.userId), payload.isOnline === true);
      return statusMap;
    }

    // Extract possible container
    const raw = payload.onlineUsers || payload.users || payload;

    // Array payloads (ids or user objects)
    if (Array.isArray(raw)) {
      raw.forEach((item: any) => {
        if (typeof item === 'string') {
          statusMap.set(item, true);
        } else if (item?._id || item?.userId) {
          const id = item._id || item.userId;
          const online = item.isOnline !== undefined ? item.isOnline : true;
          statusMap.set(String(id), online === true);
        }
      });
      return statusMap;
    }

    // Object map payloads { userId: true | { isOnline } }
    if (typeof raw === 'object') {
      Object.entries(raw).forEach(([id, value]) => {
        if (typeof value === 'boolean') {
          statusMap.set(id, value);
        } else if (value && typeof value === 'object') {
          const online = (value as any).isOnline !== undefined ? (value as any).isOnline : true;
          statusMap.set(id, online === true);
        } else {
          statusMap.set(id, true);
        }
      });
    }

    return statusMap;
  }

  /**
   * Apply normalized status map to people list and refresh data source
   */
  private applyOnlineStatusMap(statusMap: Map<string, boolean>): void {
    const updated = this.people().map(person => {
      if (!statusMap.has(person._id)) {
        return person;
      }
      const nextStatus = statusMap.get(person._id) ?? false;
      if (person.isOnline === nextStatus) {
        return person;
      }
      return { ...person, isOnline: nextStatus };
    });

    // Only update when something actually changed to avoid unnecessary re-render
    const hasChanges = updated.some((p, idx) => p !== this.people()[idx]);
    if (hasChanges) {
      this.people.set(updated);
      this.updateDataSource();
    }
  }

  /**
   * Update a single user's online status
   */
  private setUserOnlineStatus(userId: string, isOnline: boolean): void {
    const statusMap = new Map<string, boolean>();
    statusMap.set(userId, isOnline);
    this.applyOnlineStatusMap(statusMap);
  }

  // Generate a unique ID for new people
  private generateId(): string {
    return 'person_' + Math.random().toString(36).substr(2, 9);
  }

  // Get initials from person name for avatar placeholder
  getInitials(name: string): string {
    if (!name || name.trim() === '') {
      return '?';
    }

    const words = name.trim().split(' ');
    if (words.length === 1) {
      return words[0].charAt(0).toUpperCase();
    }

    return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
  }

  // Handle image loading errors
  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    img.style.display = 'none';

    // Find and show the avatar placeholder
    const avatarContainer = img.closest('.person-avatar') as HTMLElement;
    if (avatarContainer) {
      const placeholder = avatarContainer.querySelector('.avatar-placeholder') as HTMLElement;
      if (placeholder) {
        placeholder.style.display = 'flex';
      }
    }
  }

  getAvatarColor(userId: string): string {
    // Generate a consistent color based on user ID - Rich vibrant colors matching updates component
    const colors = [
      '#0644C9', // Rich Vibrant Blue
      '#8B2CF5', // Rich Purple
      '#E91E63', // Rich Pink
      '#4CAF50', // Rich Green
      '#FF9800', // Rich Orange
      '#9C27B0', // Rich Magenta
      '#3F51B5', // Rich Indigo
      '#00BCD4', // Rich Cyan
      '#795548', // Rich Brown
      '#2196F3', // Rich Blue
      '#009688', // Rich Teal
      '#673AB7', // Rich Deep Purple
      '#FF6F00', // Rich Deep Orange
      '#1976D2', // Rich Dark Blue
      '#F44336', // Rich Red
      '#FFC107', // Rich Amber
      '#8BC34A', // Rich Light Green
      '#00E676', // Rich Light Green
      '#18FFFF', // Rich Light Cyan
      '#E1BEE7', // Rich Light Purple
      '#a96970', // Rich Light Pink
      '#B39DDB', // Rich Light Purple
      '#9FA8DA', // Rich Light Indigo
      '#90CAF9', // Rich Light Blue
      '#81D4FA', // Rich Light Blue
      '#80DEEA', // Rich Light Cyan
      '#80CBC4', // Rich Light Teal
      '#A5D6A7', // Rich Light Green
      '#C8E6C9', // Rich Light Green
      '#DCEDC8', // Rich Light Green
      '#8e972d', // Rich Light Lime
      '#FFF9C4', // Rich Light Yellow
      '#FFECB3', // Rich Light Orange
      '#FFE0B2', // Rich Light Orange
      '#FFCCBC', // Rich Light Orange
      '#FFAB91', // Rich Light Orange
      '#BCAAA4', // Rich Light Brown
      '#D7CCC8', // Rich Light Brown
      '#F5F5F5', // Rich Light Grey
      '#EEEEEE', // Rich Light Grey
      '#E0E0E0', // Rich Light Grey
    ];
    const index = userId.charCodeAt(0) % colors.length;
    return colors[index];
  }

// Material Sort
sortData(sort: any): void {
  if (!sort.active) {
    // Clear sort state when no active sort
    this.currentSortColumn.set(null);
    this.currentSortDirection.set('asc');
    this.updateDataSource();
    return;
  }

  // Handle custom sort behavior - cycle between asc and desc only
  const column = sort.active;
  const currentColumn = this.currentSortColumn();
  const currentDirection = this.currentSortDirection();

  let direction: 'asc' | 'desc';

  if (column === currentColumn) {
    // Same column - toggle direction
    direction = currentDirection === 'asc' ? 'desc' : 'asc';
  } else {
    // Different column - start with ascending
    direction = 'asc';
  }

  // Update sort state
  this.currentSortColumn.set(column);
  this.currentSortDirection.set(direction);

  // Apply the sort using the extracted method
  this.applySort(column, direction);
}
}
